import { Col, Row } from 'antd'
import './ScoreSummaryTable.style.less'

interface ChapterScore {
  score: number
  weight: number
  percent: number
  section_group: string
}

interface NonCompliant {
  label?: string
  status?: boolean
  reasons?: { type: string }[]
}

interface OverallScore {
  score: number
  rating: string
  weight: number
  percent: number
  non_compliant?: NonCompliant
}

interface GradingScoreData {
  scores: ChapterScore[]
  overall_score: OverallScore
}

interface Props {
  data?: GradingScoreData
  // Company setting "fcca".non_compliant.status
  nonCompliantEnabled?: boolean
  auditRatingTitles?: {
    chapter?: string
  }
}

const NON_COMPLIANT_COLOR = '#C03261'
const DEFAULT_NON_COMPLIANT_LABEL = '(Non Compliant)'

function ScoreSummaryTable(props: Props) {
  const { data, nonCompliantEnabled = false, auditRatingTitles } = props

  const chapters: ChapterScore[] = data?.scores ?? []
  const overallScore = data?.overall_score ?? ({} as OverallScore)

  const nonCompliant = overallScore?.non_compliant
  // Shown only when the company switch is on and the API marked the audit
  const isNonCompliant = nonCompliantEnabled && nonCompliant?.status === true
  const nonCompliantLabel =
    nonCompliant?.label?.trim() || DEFAULT_NON_COMPLIANT_LABEL

  const TITLE_SPAN = isNonCompliant ? 12 : 16
  const VALUE_SPAN = 2
  const PERCENT_SPAN = isNonCompliant ? 6 : 2

  return (
    <div className="score-summary-wrapper">
      <div className="score-summary-main-header">
        Score Summary and Final Assessment
      </div>

      <div className="score-summary-table-container">
        <div className="score-summary-content">
          <Row className="table-header-row" justify="space-between">
            <Col span={TITLE_SPAN}>
              <span className="header-text text-left">
                {auditRatingTitles?.chapter ?? 'Chapter'}
              </span>
            </Col>
            <Col span={VALUE_SPAN}>
              <span className="header-text text-center">Score</span>
            </Col>
            <Col span={VALUE_SPAN}>
              <span className="header-text text-center">Weight</span>
            </Col>
            <Col span={PERCENT_SPAN}>
              <span className="header-text text-center">%</span>
            </Col>
          </Row>

          {chapters.map((chapter, index) => (
            <Row key={index} className="table-item-row" justify="space-between">
              <Col span={TITLE_SPAN}>
                <span className="item-text text-left">
                  {chapter?.section_group ?? ''}
                </span>
              </Col>
              <Col span={VALUE_SPAN}>
                <span className="item-text text-center">
                  {chapter?.score ?? 0}
                </span>
              </Col>
              <Col span={VALUE_SPAN}>
                <span className="item-text text-center">
                  {chapter?.weight ?? 0}
                </span>
              </Col>
              <Col span={PERCENT_SPAN}>
                <span className="item-text text-center">
                  {chapter?.percent ?? 0}%
                </span>
              </Col>
            </Row>
          ))}

          <Row className="table-final-row" justify="space-between">
            <Col span={TITLE_SPAN}>
              <span className="final-text text-left">FINAL ASSESSMENT</span>
            </Col>
            <Col span={VALUE_SPAN}>
              <span className="final-text text-center">
                {overallScore?.score ?? 0}
              </span>
            </Col>
            <Col span={VALUE_SPAN}>
              <span className="final-text text-center">
                {overallScore?.weight ?? 0}
              </span>
            </Col>
            <Col span={PERCENT_SPAN}>
              <span
                className="final-text text-center"
                // Inline colour: the PDF is styled by the API's copy of main.css,
                // which has no rule for a new class.
                style={
                  isNonCompliant ? { color: NON_COMPLIANT_COLOR } : undefined
                }
              >
                {overallScore?.percent ?? 0}%
                {isNonCompliant && ` ${nonCompliantLabel}`}
              </span>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  )
}

export default ScoreSummaryTable
